// src/pages/dashboards/admin/UserDetailModal.jsx
// User detail + admin actions, styled to match the company modal: avatar header,
// filled stat tiles, scan-pool hero, two-column access controls.

import React, { useEffect, useState } from "react";
import {
  getUser, setUserRole, setUserActive, setUserTier,
  getScanCredits, allocateScanCredits,
} from "../../../api/admin";
import { Modal, Field, Select, Input, Button, Badge, Loading } from "../../../components/ui";
import { useToast } from "../../../context/ToastContext";
import { dateTime, relative, titleCase } from "../../../lib/format";

const ROLES = ["user", "practitioner", "company", "admin"];
const TIERS = ["free", "starter", "pro"];
// personal tier limits, matches tiers.js defaults (used as fallback display)
const TIER_LIMIT = { free: 15, starter: 125, pro: 300, admin: 500 };

function Tile({ label, value }) {
  return (
    <div className="rounded-xl bg-tb-bg-2 dark:bg-tb-d-inset p-4 text-center">
      <p className="text-[11px] uppercase tracking-wider text-tb-muted">{label}</p>
      <p className="mt-1 text-2xl font-bold">{value}</p>
    </div>
  );
}

function SectionTitle({ children }) {
  return <p className="text-xs uppercase tracking-wider text-tb-muted mb-3">{children}</p>;
}

export default function UserDetailModal({ userId, currentAdminId, onClose, onChanged }) {
  const toast = useToast();
  const [user, setUser] = useState(null);
  const [pool, setPool] = useState(null); // { used, limit, remaining, credits }
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const [role, setRole] = useState("");
  const [tier, setTier] = useState("");
  const [months, setMonths] = useState("");
  const [creditDelta, setCreditDelta] = useState("");

  const load = async () => {
    setErr("");
    try {
      const u = await getUser(userId);
      setUser(u);
      setRole(u.role);
      setTier(u.tier);
      const c = await getScanCredits({ user_id: userId });
      if (u.pool) {
        setPool({ ...u.pool, credits: u.pool.credits ?? c.balance });
      } else {
        setPool({ used: null, limit: TIER_LIMIT[u.tier] ?? null, remaining: null, credits: c.balance });
      }
    } catch (e) {
      setErr(e.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [userId]);

  const isSelf = user && user.id === currentAdminId;

  const saveRole = async () => {
    setSaving(true);
    try {
      await setUserRole(userId, role);
      toast.success("Role updated");
      onChanged?.();
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const toggleActive = async () => {
    setSaving(true);
    try {
      const next = !user.is_active;
      await setUserActive(userId, next);
      toast.success(next ? "Account enabled" : "Account disabled");
      onChanged?.();
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const saveTier = async () => {
    setSaving(true);
    try {
      await setUserTier(userId, tier, months ? parseInt(months, 10) : undefined);
      toast.success("Tier updated");
      setMonths("");
      onChanged?.();
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const applyCredits = async () => {
    const amount = parseInt(creditDelta, 10);
    if (!amount) { toast.error("Enter a non-zero amount"); return; }
    setSaving(true);
    try {
      await allocateScanCredits({ user_id: userId, amount });
      setCreditDelta("");
      toast.success("Credits updated");
      load();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const pct = pool && pool.limit && pool.used != null
    ? Math.min(100, Math.round((pool.used / pool.limit) * 100))
    : null;

  const initial = (user?.name || "?").trim().charAt(0).toUpperCase();

  return (
    <Modal open onClose={onClose} title="User" width="max-w-4xl" bodyClassName="p-6 sm:p-8">
      {err && <p className="text-sm text-red-600 mb-4">{err}</p>}
      {!user ? (
        <Loading label="Loading user" />
      ) : (
        <div className="space-y-7">
          {/* identity header */}
          <div className="flex items-center gap-4">
            <span
              className="grid place-items-center h-14 w-14 rounded-2xl text-white text-xl font-bold shrink-0 shadow-green-glow"
              style={{ background: "linear-gradient(135deg,#12B266,#2AD17B)" }}
            >
              {initial}
            </span>
            <div className="min-w-0">
              <h2 className="text-xl font-bold truncate">{user.name}</h2>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Badge tone={user.is_active ? "green" : "red"}>{user.is_active ? "Active" : "Disabled"}</Badge>
                <Badge tone="gray">{titleCase(user.role)}</Badge>
                <span className="text-xs text-tb-muted truncate">{user.email}{user.phone ? ` · ${user.phone}` : ""}</span>
              </div>
            </div>
          </div>

          {/* counts */}
          <div className="grid grid-cols-3 gap-3">
            <Tile label="Receipts" value={user.counts?.receipts ?? 0} />
            <Tile label="Expenses" value={user.counts?.expenses ?? 0} />
            <Tile label="Scans" value={user.counts?.scans ?? 0} />
          </div>

          <p className="text-xs text-tb-muted">
            Joined {dateTime(user.created_at)} · Last login {relative(user.last_login_at)}
          </p>

          {/* two-column main */}
          <div className="grid lg:grid-cols-2 gap-7">
            {/* access controls */}
            <div className="space-y-4">
              <SectionTitle>Access</SectionTitle>

              <div className="grid sm:grid-cols-[1fr_auto] gap-2 items-end">
                <Field label="Role">
                  <Select value={role} onChange={(e) => setRole(e.target.value)} disabled={isSelf}>
                    {ROLES.map((r) => <option key={r} value={r}>{titleCase(r)}</option>)}
                  </Select>
                </Field>
                <Button variant="subtle" onClick={saveRole} disabled={saving || role === user.role || isSelf}>
                  Save
                </Button>
              </div>

              <div className="grid sm:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <Field label="Tier" hint="overrides subscription">
                  <Select value={tier} onChange={(e) => setTier(e.target.value)}>
                    {TIERS.map((t) => <option key={t} value={t}>{titleCase(t)}</option>)}
                  </Select>
                </Field>
                <Field label="Months" hint="optional term">
                  <Input type="number" min="1" max="60" value={months}
                         onChange={(e) => setMonths(e.target.value)} placeholder="e.g. 12" />
                </Field>
                <Button variant="subtle" onClick={saveTier} disabled={saving} className="sm:mb-[22px]">Apply</Button>
              </div>

              {isSelf && <p className="text-xs text-tb-muted">You cannot change your own role or status.</p>}
            </div>

            {/* scan pool hero */}
            <div>
              <SectionTitle>Scan pool</SectionTitle>
              <div className="rounded-2xl p-5 bg-tb-wash dark:bg-tb-brand/10 border border-tb-brand/20">
                <div className="flex items-end justify-between">
                  <div>
                    <p className="text-xs uppercase tracking-wider text-tb-brand/80">Remaining</p>
                    <p className="text-4xl font-bold text-tb-brand leading-none mt-1">
                      {pool?.remaining != null ? pool.remaining : (pool?.limit ?? "—")}
                    </p>
                  </div>
                  {pool?.credits > 0 && (
                    <span className="text-xs rounded-full bg-white/70 dark:bg-white/10 px-2 py-1 text-tb-brand font-medium">
                      +{pool.credits} credits
                    </span>
                  )}
                </div>

                <div className="mt-4 h-2 rounded-full bg-white/60 dark:bg-black/20 overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all ${pct >= 90 ? "bg-red-500" : "bg-tb-brand"}`}
                    style={{ width: `${pct != null ? pct : 0}%` }}
                  />
                </div>

                <div className="mt-3 flex justify-between text-xs text-tb-muted">
                  <span>Used {pool?.used != null ? pool.used : "—"}</span>
                  <span>Limit {pool?.limit ?? "—"}</span>
                </div>

                {pool?.used == null && (
                  <p className="mt-2 text-[11px] text-tb-muted">Live usage appears once the API reports it.</p>
                )}
              </div>

              <div className="mt-4">
                <Field label="Allocate credits" hint="positive tops up, negative corrects">
                  <div className="flex gap-2">
                    <Input type="number" value={creditDelta} onChange={(e) => setCreditDelta(e.target.value)} placeholder="e.g. 50 or -10" />
                    <Button variant="subtle" onClick={applyCredits} disabled={saving}>Allocate</Button>
                  </div>
                </Field>
              </div>
            </div>
          </div>

          {/* active toggle */}
          <div className="flex items-center justify-between gap-4 pt-5 border-t border-tb-border dark:border-tb-d-border">
            <span className="text-sm text-tb-muted">
              {user.is_active ? "This account can sign in." : "This account is disabled."}
            </span>
            <Button
              variant={user.is_active ? "danger" : "primary"}
              onClick={toggleActive}
              disabled={saving || isSelf}
            >
              {user.is_active ? "Disable account" : "Enable account"}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}